import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/common/BackButton';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { useRegisterContestentMutation } from '@/store/api/accountsApi';
import { useState } from 'react';
import UploadImage from '@/components/common/UploadImage';

/* -------------------- Schema -------------------- */

const investorSchema = z.object({
  firstname: z.string().min(1, 'First name is required'),
  lastname: z.string().min(1, 'Last name is required'),
  instagram: z.string().optional(),
  linkedin: z.string().optional(),
  twitter: z.string().optional(),
  youtube: z.string().optional(),
  accountType: z.enum(['PARTNER', 'CONTESTANT', 'TRIBE', 'COMMUNITY']),
  profileImage: z.string().min(1, 'Profile Image is required'),
});

type InvestorFormData = z.infer<typeof investorSchema>;

/* -------------------- Component -------------------- */

const CreateContestant = () => {
  const navigate = useNavigate();
  const [isImageUploading, setIsImageUploading] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<InvestorFormData>({
    resolver: zodResolver(investorSchema),
    defaultValues: {
      firstname: '',
      lastname: '',
      instagram: '',
      linkedin: '',
      twitter: '',
      youtube: '',
      accountType: 'CONTESTANT',
      profileImage: '',
    },
  });

  const [registerContestent, { isLoading, error }] = useRegisterContestentMutation();

  // const [selectedCountryId, setSelectedCountryId] = useState('1');

  // const handleCountryChange = (value: string) => {
  //   const country = countryList?.find((c) => String(c.id) === value);
  //   if (!country) return;

  //   // setSelectedCountryId(value);
  // };

  const onSubmit = async (data: InvestorFormData) => {
    try {
      const payload = {
        firstname: data.firstname,
        lastname: data.lastname,
        accountType: 'CONTESTANT',
        profileImage: data.profileImage.split('/').pop() || '',
        ...(data.instagram && { instagram: data.instagram }),
        ...(data.linkedin && { linkedin: data.linkedin }),
        ...(data.twitter && { twitter: data.twitter }),
        ...(data.youtube && { youtube: data.youtube }),
      };
      await registerContestent(payload).unwrap();
      navigate(-1);
    } catch (err) {
      console.error('Register User Failed:', err);
    }
  };

  return (
    <div className="space-y-8 xl:max-w-[80%]">
      <div className="flex items-center gap-5 mb-4">
        <BackButton />
        <h2 className="text-2xl font-bold">Add Contestant</h2>
      </div>

      {error && (
        <ErrorBanner
          type="error"
          title="Error Creating Contestant"
          message={(error as any)?.data?.message || 'Something went wrong'}
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-2 gap-x-10 gap-y-6">
          <div className="space-y-2">
            <Label>First Name</Label>
            <Input {...register('firstname')} />
            {errors.firstname && (
              <p className="text-sm text-destructive">{errors.firstname.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Last Name</Label>
            <Input {...register('lastname')} />
            {errors.lastname && (
              <p className="text-sm text-destructive">{errors.lastname.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Instagram Profile</Label>
            <Input {...register('instagram')} />
            {errors.instagram && (
              <p className="text-sm text-destructive">{errors.instagram.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Linkedin Profile</Label>
            <Input {...register('linkedin')} />
            {errors.linkedin && (
              <p className="text-sm text-destructive">{errors.linkedin.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Twitter Profile</Label>
            <Input {...register('twitter')} />
            {errors.twitter && <p className="text-sm text-destructive">{errors.twitter.message}</p>}
          </div>
          <div className="space-y-2">
            <Label>Youtube Profile</Label>
            <Input {...register('youtube')} />
            {errors.youtube && <p className="text-sm text-destructive">{errors.youtube.message}</p>}
          </div>

          <div className="space-y-2">
            <UploadImage
              label="Profile Image"
              onChange={(url) => {
                setValue('profileImage', url, { shouldValidate: true });
              }}
              setIsImageUploading={setIsImageUploading}
            />
            {errors.profileImage && (
              <p className="text-sm text-destructive">{errors.profileImage.message}</p>
            )}
          </div>
          {/* <div className="space-y-2">
            <Label>Country</Label>
            <Select value={selectedCountryId} onValueChange={handleCountryChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {countryList?.map((country) => (
                  <SelectItem key={country.id} value={String(country.id)}>
                    {country.flag} {country.name} ({country.dialCode})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div> */}
        </div>

        <Button type="submit" disabled={isLoading || isImageUploading} className="my-5">
          Add Contestant
        </Button>
      </form>
    </div>
  );
};

export default CreateContestant;
